import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Phone,
  Mic,
  Settings,
  Key,
  Shield,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Save,
  TestTube,
  RefreshCw,
  Globe
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminSettings() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Telephony settings
  const [telephonySettings, setTelephonySettings] = useState({
    provider: "telnyx",
    api_key: "",
    api_secret: "",
    sip_connection_id: "",
    phone_numbers: [],
    webhook_url: "",
    is_configured: false,
    last_tested: null,
    test_status: null
  });
  
  // Voice settings
  const [voiceSettings, setVoiceSettings] = useState({
    tts_provider: "elevenlabs",
    tts_api_key: "",
    tts_voice_id: "",
    tts_fallback_enabled: true,
    stt_provider: "openai_whisper",
    stt_api_key: "",
    is_configured: false,
    last_tested: null,
    test_status: null
  });
  
  // Platform settings
  const [platformSettings, setPlatformSettings] = useState({
    platform_name: "Speakr",
    support_email: "info@gskproductions.ca",
    default_safe_mode: true,
    max_concurrent_calls: 100,
    recording_enabled: true,
    consent_required: true,
    confidence_threshold: 0.75
  });
  
  const [showKeys, setShowKeys] = useState({});
  const [newPhoneNumber, setNewPhoneNumber] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const [telRes, voiceRes, platformRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/settings/telephony`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/settings/voice`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/settings/platform`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (telRes.ok) {
        const data = await telRes.json();
        setTelephonySettings(prev => ({ ...prev, ...data }));
      }
      if (voiceRes.ok) {
        const data = await voiceRes.json();
        setVoiceSettings(prev => ({ ...prev, ...data }));
      }
      if (platformRes.ok) {
        const data = await platformRes.json();
        setPlatformSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveTelephonySettings = async () => {
    setSaving(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/telephony`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(telephonySettings)
      });
      
      if (res.ok) {
        toast.success("Telephony settings saved");
        loadSettings();
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save telephony settings");
    } finally {
      setSaving(false);
    }
  };

  const saveVoiceSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/voice`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(voiceSettings)
      });
      
      if (res.ok) {
        toast.success("Voice settings saved");
        loadSettings();
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save voice settings");
    } finally {
      setSaving(false);
    }
  };

  const savePlatformSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/platform`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(platformSettings)
      });
      
      if (res.ok) {
        toast.success("Platform settings saved");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save platform settings");
    } finally {
      setSaving(false);
    }
  };

  const testTelephonyConnection = async () => {
    setTesting(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/telephony/test`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(`Test ${result.status}: ${result.message}`);
        loadSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Test failed");
      }
    } catch (error) {
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const addPhoneNumber = () => {
    if (newPhoneNumber && !telephonySettings.phone_numbers.includes(newPhoneNumber)) {
      setTelephonySettings(prev => ({
        ...prev,
        phone_numbers: [...prev.phone_numbers, newPhoneNumber]
      }));
      setNewPhoneNumber("");
    }
  };

  const removePhoneNumber = (number) => {
    setTelephonySettings(prev => ({
      ...prev,
      phone_numbers: prev.phone_numbers.filter(n => n !== number)
    }));
  };

  const toggleShowKey = (key) => {
    setShowKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Platform Settings</h1>
          <p className="text-slate-500 mt-1">Manage API credentials and system configuration</p>
        </div>
        <Button variant="outline" onClick={loadSettings} data-testid="refresh-settings">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Telephony Settings */}
      <Card data-testid="telephony-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle>Telephony Provider</CardTitle>
                <CardDescription>Configure Telnyx for voice calls</CardDescription>
              </div>
            </div>
            <Badge 
              variant={telephonySettings.is_configured ? "default" : "secondary"}
              className={telephonySettings.is_configured ? "bg-emerald-100 text-emerald-700" : ""}
            >
              {telephonySettings.is_configured ? (
                <><CheckCircle className="w-3 h-3 mr-1" /> Configured</>
              ) : (
                <><XCircle className="w-3 h-3 mr-1" /> Not Configured</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="telnyx_api_key">Telnyx API Key</Label>
              <div className="relative">
                <Input
                  id="telnyx_api_key"
                  type={showKeys.telnyxApi ? "text" : "password"}
                  value={telephonySettings.api_key}
                  onChange={(e) => setTelephonySettings(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="KEY..."
                  data-testid="telnyx-api-key-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => toggleShowKey("telnyxApi")}
                >
                  {showKeys.telnyxApi ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sip_connection_id">SIP Connection ID</Label>
              <Input
                id="sip_connection_id"
                value={telephonySettings.sip_connection_id}
                onChange={(e) => setTelephonySettings(prev => ({ ...prev, sip_connection_id: e.target.value }))}
                placeholder="Enter SIP Connection ID"
                data-testid="sip-connection-id-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Phone Numbers</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {telephonySettings.phone_numbers.map((number) => (
                <Badge key={number} variant="outline" className="px-3 py-1">
                  {number}
                  <button
                    onClick={() => removePhoneNumber(number)}
                    className="ml-2 text-slate-400 hover:text-red-500"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                placeholder="+1234567890"
                className="max-w-xs"
              />
              <Button variant="outline" onClick={addPhoneNumber} size="sm">
                Add Number
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button onClick={saveTelephonySettings} disabled={saving} data-testid="save-telephony-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Telephony Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={testTelephonyConnection}
              disabled={testing || !telephonySettings.api_key}
              data-testid="test-telephony-btn"
            >
              <TestTube className="w-4 h-4 mr-2" />
              Test Connection
            </Button>
            {telephonySettings.last_tested && (
              <span className="text-sm text-slate-500">
                Last tested: {new Date(telephonySettings.last_tested).toLocaleString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Voice/TTS Settings */}
      <Card data-testid="voice-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Mic className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle>Voice & TTS Provider</CardTitle>
                <CardDescription>Configure ElevenLabs for text-to-speech</CardDescription>
              </div>
            </div>
            <Badge 
              variant={voiceSettings.is_configured ? "default" : "secondary"}
              className={voiceSettings.is_configured ? "bg-emerald-100 text-emerald-700" : ""}
            >
              {voiceSettings.is_configured ? (
                <><CheckCircle className="w-3 h-3 mr-1" /> Configured</>
              ) : (
                <><XCircle className="w-3 h-3 mr-1" /> Not Configured</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="elevenlabs_api_key">ElevenLabs API Key</Label>
              <div className="relative">
                <Input
                  id="elevenlabs_api_key"
                  type={showKeys.elevenlabsApi ? "text" : "password"}
                  value={voiceSettings.tts_api_key}
                  onChange={(e) => setVoiceSettings(prev => ({ ...prev, tts_api_key: e.target.value }))}
                  placeholder="sk_..."
                  data-testid="elevenlabs-api-key-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  onClick={() => toggleShowKey("elevenlabsApi")}
                >
                  {showKeys.elevenlabsApi ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="voice_id">Voice ID (optional)</Label>
              <Input
                id="voice_id"
                value={voiceSettings.tts_voice_id}
                onChange={(e) => setVoiceSettings(prev => ({ ...prev, tts_voice_id: e.target.value }))}
                placeholder="Default voice will be used"
                data-testid="voice-id-input"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Switch
              id="fallback_enabled"
              checked={voiceSettings.tts_fallback_enabled}
              onCheckedChange={(checked) => setVoiceSettings(prev => ({ ...prev, tts_fallback_enabled: checked }))}
            />
            <Label htmlFor="fallback_enabled">
              Enable OpenAI TTS fallback (uses platform LLM key)
            </Label>
          </div>
          
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button onClick={saveVoiceSettings} disabled={saving} data-testid="save-voice-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Voice Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Platform Settings */}
      <Card data-testid="platform-settings-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Globe className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <CardTitle>Platform Configuration</CardTitle>
              <CardDescription>General platform settings and defaults</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="platform_name">Platform Name</Label>
              <Input
                id="platform_name"
                value={platformSettings.platform_name}
                onChange={(e) => setPlatformSettings(prev => ({ ...prev, platform_name: e.target.value }))}
                data-testid="platform-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="support_email">Support Email</Label>
              <Input
                id="support_email"
                type="email"
                value={platformSettings.support_email}
                onChange={(e) => setPlatformSettings(prev => ({ ...prev, support_email: e.target.value }))}
                data-testid="support-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="max_concurrent_calls">Max Concurrent Calls</Label>
              <Input
                id="max_concurrent_calls"
                type="number"
                value={platformSettings.max_concurrent_calls}
                onChange={(e) => setPlatformSettings(prev => ({ ...prev, max_concurrent_calls: parseInt(e.target.value) }))}
                data-testid="max-calls-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confidence_threshold">AI Confidence Threshold</Label>
              <Input
                id="confidence_threshold"
                type="number"
                step="0.05"
                min="0"
                max="1"
                value={platformSettings.confidence_threshold}
                onChange={(e) => setPlatformSettings(prev => ({ ...prev, confidence_threshold: parseFloat(e.target.value) }))}
                data-testid="confidence-threshold-input"
              />
              <p className="text-xs text-slate-500">Actions below this threshold require human approval</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
            <div className="flex items-center gap-3">
              <Switch
                id="default_safe_mode"
                checked={platformSettings.default_safe_mode}
                onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, default_safe_mode: checked }))}
              />
              <Label htmlFor="default_safe_mode">Default Safe Mode for new clients</Label>
            </div>
            
            <div className="flex items-center gap-3">
              <Switch
                id="recording_enabled"
                checked={platformSettings.recording_enabled}
                onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, recording_enabled: checked }))}
              />
              <Label htmlFor="recording_enabled">Enable call recording</Label>
            </div>
            
            <div className="flex items-center gap-3">
              <Switch
                id="consent_required"
                checked={platformSettings.consent_required}
                onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, consent_required: checked }))}
              />
              <Label htmlFor="consent_required">Require consent</Label>
            </div>
          </div>
          
          <div className="flex items-center gap-3 pt-4 border-t">
            <Button onClick={savePlatformSettings} disabled={saving} data-testid="save-platform-btn">
              <Save className="w-4 h-4 mr-2" />
              Save Platform Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
